import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
    ReferralState,
    PearlsCommissionRequest,
    PearlsCommissionResponse,
    PearlsCommissionDetailResponse,
    InfinityCommissionRequest,
    InfinityCommissionResponse,
    UnityCommissionRequest,
    UnityCommissionResponse,
    CommunityGrowthCommissionRequest,
    CommunityGrowthCommissionResponse,
    MilestoneCommissionRequest,
    MilestoneCommissionResponse,
    MilestoneCommissionDetailResponse,
    ReferralCommissionRequest,
    ReferralCommissionResponse,
    DailyCommissionRequest,
    DailyCommissionResponse,
    FlowCommissionResponse,
    ReferralNetworkResponse,
    NovaReferralNetworkResponse,
    ReferralTreeResponse,
    DailyCommissionDetailResponse,
    InfinityCommissionDetailResponse,
    PartnerGrowthItem,
    PartnerGrowthResponse,
    PartnerGrowthRequest,
    PartnerGrowthIncentivesRequest,
    PartnerGrowthIncentivesResponse,
    ReferralNetworkMilestoneMembersRequest,
    ReferralNetworkMilestoneMembersResponse,
    FlowCommissionMonthlyRequest,
    FlowCommissionMonthlyResponse,
    ReferralEarningsSummaryResponse,
    LoyaltyReferralCommissionRequest,
    LoyaltyReferralCommissionResponse,
    ShoppingVoucherRequest,
    ShoppingVoucherResponse,
} from "./types";

const API_URL = process.env.REACT_APP_API_BASE_URL || "";

// Axios instance with cookies support
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
    async (config) => {
        let token = getCookie("access_token");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const getPearlsCommission = createAsyncThunk(
    "referral/getPearlsCommission",
    async (params: PearlsCommissionRequest, { rejectWithValue }) => {
        try {
            console.log("getPearlsCommission called with params:", params);
            console.log("API_URL:", API_URL);
            const response = await api.get<PearlsCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/pearls-commission",
                { params }
            );
            console.log("getPearlsCommission response:", response.data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch pearls commission data"
            );
        }
    }
);

export const getPearlsCommissionDetail = createAsyncThunk(
    "referral/getPearlsCommissionDetail",
    async (recordId: string, { rejectWithValue }) => {
        try {
            const response = await api.get<PearlsCommissionDetailResponse>(
                `/api/v1/referral-mlm/customer/my-referral-earnings/pearls-commission/${recordId}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch pearls commission detail"
            );
        }
    }
);

export const getInfinityCommission = createAsyncThunk(
    "referral/getInfinityCommission",
    async (params: InfinityCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<InfinityCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/infinity-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch infinity commission data"
            );
        }
    }
);

// New Thunk for Infinity Commission Details (by Month)
export const getInfinityCommissionDetails = createAsyncThunk(
    "referral/getInfinityCommissionDetails",
    async ({ month, ...params }: { month: string } & InfinityCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<InfinityCommissionDetailResponse>(
                `/api/v1/referral-mlm/customer/my-referral-earnings/infinity-commission/${month}`,
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch infinity commission details"
            );
        }
    }
);

export const getUnityCommission = createAsyncThunk(
    "referral/getUnityCommission",
    async (params: UnityCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<UnityCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/unity-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch unity commission data"
            );
        }
    }
);

export const getCommunityGrowthCommission = createAsyncThunk(
    "referral/getCommunityGrowthCommission",
    async (params: CommunityGrowthCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<CommunityGrowthCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/community-growth-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch community growth commission data"
            );
        }
    }
);

export const getMilestoneCommission = createAsyncThunk(
    "referral/getMilestoneCommission",
    async (params: MilestoneCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<MilestoneCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/milestone-referral-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch milestone commission data"
            );
        }
    }
);

export const getMilestoneCommissionDetail = createAsyncThunk(
    "referral/getMilestoneCommissionDetail",
    async (recordId: string, { rejectWithValue }) => {
        try {
            const response = await api.get<MilestoneCommissionDetailResponse>(
                `/api/v1/referral-mlm/customer/my-referral-earnings/milestone-referral-commission/${recordId}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch milestone commission detail"
            );
        }
    }
);

export const getReferralCommission = createAsyncThunk(
    "referral/getReferralCommission",
    async (params: ReferralCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<ReferralCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/referral-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch referral commission data"
            );
        }
    }
);

export const getDailyCommission = createAsyncThunk(
    "referral/getDailyCommission",
    async (params: DailyCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<DailyCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/daily-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch daily commission data"
            );
        }
    }
);

// New Thunk for Daily Commission Details (by Month)
export const getDailyCommissionDetails = createAsyncThunk(
    "referral/getDailyCommissionDetails",
    async ({ month, ...params }: { month: string } & DailyCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<DailyCommissionDetailResponse>(
                `/api/v1/referral-mlm/customer/my-referral-earnings/daily-commission/${month}`,
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch daily commission details"
            );
        }
    }
);

export const getFlowCommission = createAsyncThunk(
    "referral/getFlowCommission",
    async (params: any, { rejectWithValue }) => {
        try {
            const response = await api.get<FlowCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/flow-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch flow commission data"
            );
        }
    }
);

export const getReferralNetwork = createAsyncThunk(
    "referral/getReferralNetwork",
    async ({ orderNo, ...params }: any, { rejectWithValue }) => {
        try {
            const searchVal = params.search || "";
            const apiParams: any = {
                page: params.page || 1,
                limit: params.limit || 10,
                status: params.status || "ALL",
                sortOrder: params.sortOrder || "DESC",
            };
            if (searchVal) {
                apiParams.search = searchVal;
                if (/^[sS]\d/.test(searchVal)) {
                    apiParams.registerId = searchVal;
                } else {
                    apiParams.memberName = searchVal;
                }
            }
            if (params.startDate) apiParams.startDate = params.startDate;
            if (params.endDate) apiParams.endDate = params.endDate;

            // 1. Try dedicated flow commission referral network endpoint
            try {
                const res = await api.get(
                    `/api/v1/referral-mlm/customer/my-referral-earnings/flow-commission/${orderNo}/referral-network`,
                    { params: apiParams }
                );
                const resData = res.data.data || res.data;
                if (resData && (resData.network || resData.levels || resData.orderDetails)) {
                    return {
                        success: true,
                        orderDetails: resData.orderDetails || {
                            orderId: orderNo,
                            orderNo: orderNo,
                            status: "Active",
                        },
                        levels: resData.levels || [],
                        combinedLevels: resData.combinedLevels || [],
                        showCombinedLevels: resData.showCombinedLevels || false,
                        network: resData.network || {
                            networkName: "Salar Flow Network",
                            activeContributors: 0,
                            totalContributionRequirement: 6,
                            milestones: resData.levels || [],
                            summary: {},
                            permissions: { canWithdraw: true, canTransfer: true }
                        },
                        pagination: resData.pagination || {
                            page: params.page || 1,
                            limit: params.limit || 10,
                            total: resData.network?.milestones?.length || 0,
                            totalPages: 1
                        }
                    };
                }
            } catch (endpointErr) {
                console.warn("Primary flow-commission referral network API fallback:", endpointErr);
            }

            // 2. Fallback to order-network-details if needed
            const response = await api.get(
                `/api/v1/referral-mlm/customer/my-referral-earnings/order-network-details/${orderNo}`,
                { params: apiParams }
            );

            const data = response.data.data || response.data;
            const milestones: any[] = [];
            const ownerRegisterId = data.ownerDetails?.registerId;
            const ownerUserName = data.ownerDetails?.fullName;

            if (data.referralNetwork) {
                const W = data.referralNetwork.width || 2;
                const D = data.referralNetwork.depth || 2;
                const members = data.referralNetwork.members || [];
                const product = data.novaNetwork?.productName || "PRODUCT 1";

                for (let l = 1; l <= D; l++) {
                    const req = Math.pow(W, l);
                    const lvlMembers = members.filter((m: any) => m.level === l);
                    const joined = lvlMembers.length;
                    milestones.push({
                        milestoneNo: l,
                        milestoneName: `Level ${l} Milestone`,
                        contributionRequirement: req,
                        activeContributors: joined,
                        status: joined >= req ? "Completed" : "Pending",
                        members: lvlMembers,
                        productName: product,
                        isAutoUpgrade: true,
                    });
                }
            } else if (data.novaNetwork) {
                const levelsSummary = data.novaNetwork.levelsSummary || [];
                levelsSummary.forEach((ls: any) => {
                    milestones.push({
                        milestoneNo: ls.levelNo,
                        milestoneName: `Level ${ls.levelNo} Milestone`,
                        contributionRequirement: ls.requiredMembers,
                        activeContributors: ls.joinedMembers,
                        status: ls.status === "COMPLETED" ? "Completed" : "Pending",
                        productName: data.novaNetwork.productName,
                        isAutoUpgrade: true,
                    });
                });
            }

            const mappedResponse = {
                success: true,
                orderDetails: {
                    orderId: data.orderNo,
                    orderNo: data.orderNo,
                    orderDate: data.ownerDetails?.doj || "",
                    orderDateRaw: data.ownerDetails?.doj || "",
                    productName: data.novaNetwork?.productName || "PRODUCT 1",
                    orderType: "REFERRAL",
                    sfNetworkNo: "1",
                    status: "Active",
                },
                network: {
                    networkName: data.novaNetwork?.novaPlanName || "Salar Flow Network",
                    activeContributors: data.referralNetwork?.currentMembers || data.novaNetwork?.totalUniqueMembers || 0,
                    totalContributionRequirement: data.referralNetwork?.maxMembers || 6,
                    milestones: milestones,
                    summary: {
                        totalMembers: data.referralNetwork?.maxMembers || 6,
                        completedMembers: data.referralNetwork?.currentMembers || 0,
                        pendingMembers: (data.referralNetwork?.maxMembers || 6) - (data.referralNetwork?.currentMembers || 0),
                        completedMilestones: milestones.filter(m => m.status === "Completed").length,
                        totalMilestones: milestones.length,
                        totalFlowIncentives: 0,
                        totalSalarPearls: 0,
                        networkStatus: data.referralNetwork?.status || "Active",
                    },
                    permissions: {
                        canWithdraw: true,
                        canTransfer: true,
                        description: "",
                    }
                },
                pagination: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    total: milestones.length,
                    totalPages: 1,
                }
            };

            return mappedResponse as any;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch referral network data"
            );
        }
    }
);

export const getNovaReferralNetwork = createAsyncThunk(
    "referral/getNovaReferralNetwork",
    async ({ orderNo, ...params }: any, { rejectWithValue }) => {
        try {
            const searchVal = params.search || "";
            const apiParams: any = {};
            if (searchVal) {
                if (/^[sS]\d/.test(searchVal)) {
                    apiParams.registerId = searchVal;
                } else {
                    apiParams.memberName = searchVal;
                }
            }

            const response = await api.get(
                `/api/v1/referral-mlm/customer/my-referral-earnings/order-network-details/${orderNo}`,
                { params: apiParams }
            );

            const data = response.data.data || response.data;
            const milestones: any[] = [];
            const ownerRegisterId = data.ownerDetails?.registerId;
            const ownerUserName = data.ownerDetails?.fullName;

            if (data.novaNetwork) {
                const levelsSummary = data.novaNetwork.levelsSummary || [];
                const members = data.novaNetwork.members || [];
                const totalRequirement = levelsSummary.reduce((sum: number, ls: any) => sum + (ls.requiredMembers || 0), 0) || 14;

                levelsSummary.forEach((ls: any) => {
                    const lvl = ls.levelNo;
                    const lvlMembers = members.filter((m: any) => m.level === lvl);

                    milestones.push({
                        milestoneNo: lvl,
                        milestoneName: `Level ${lvl} Milestone`,
                        contributionRequirement: ls.requiredMembers,
                        activeContributors: ls.joinedMembers,
                        status: ls.status === "COMPLETED" ? "Completed" : "Pending",
                        productName: data.novaNetwork.productName,
                        isAutoUpgrade: true,
                        members: lvlMembers.map((member: any, idx: number) => ({
                            sNo: idx + 1,
                            memberId: member.userId,
                            memberName: member.userName,
                            memberPhoto: "",
                            partnerNetworkId: member.registerId,
                            referringPartnerId: ownerRegisterId || "—",
                            referralUplinerId: ownerRegisterId || "—",
                            referralUplinerName: ownerUserName || "—",
                            joinedDate: member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-GB') : "—",
                            joinedDateRaw: member.createdAt || "",
                            level: member.level,
                            status: "Completed",
                            receivedIncentiveDetails: {
                                salarFlowIncentives: member.receivedIncentiveDetails?.salarFlowIncentives ?? member.commissionAmount ?? (ls.commissionAmount || 0),
                                salarPearlsIncentives: member.receivedIncentiveDetails?.salarPearlsIncentives ?? member.salarCoins ?? (ls.salarCoins || 0),
                                novaReferralCommission: member.receivedIncentiveDetails?.novaReferralCommission ?? member.salarCoins ?? (ls.commission || ls.commissionAmount || 0),
                            }
                        })),
                    });
                });
            }

            const totalPlanRequirement = data.novaNetwork?.levelsSummary?.reduce((sum: number, ls: any) => sum + (ls.requiredMembers || 0), 0) || 14;

            const mappedResponse = {
                success: true,
                orderDetails: {
                    orderId: data.orderNo,
                    orderNo: data.orderNo,
                    orderDate: data.ownerDetails?.doj || "",
                    orderDateRaw: data.ownerDetails?.doj || "",
                    productName: data.novaNetwork?.productName || "PRODUCT 1",
                    orderType: "NOVA",
                    sfNetworkNo: "1",
                    status: "Active",
                },
                network: {
                    networkName: data.novaNetwork?.novaPlanName || "Nova Referral Network",
                    activeContributors: data.novaNetwork?.totalUniqueMembers || 0,
                    totalContributionRequirement: totalPlanRequirement,
                    milestones: milestones,
                    summary: {
                        totalMembers: totalPlanRequirement,
                        completedMembers: data.novaNetwork?.totalUniqueMembers || 0,
                        pendingMembers: Math.max(0, totalPlanRequirement - (data.novaNetwork?.totalUniqueMembers || 0)),
                        completedMilestones: milestones.filter(m => m.status === "Completed").length,
                        totalMilestones: milestones.length,
                        totalFlowIncentives: 0,
                        totalSalarPearls: 0,
                        networkStatus: "Active",
                    },
                    permissions: {
                        canWithdraw: true,
                        canTransfer: true,
                        description: "",
                    }
                },
                pagination: {
                    page: params.page || 1,
                    limit: params.limit || 10,
                    total: milestones.length,
                    totalPages: 1,
                }
            };

            return mappedResponse as any;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch nova referral network data"
            );
        }
    }
);

export const getReferralTree = createAsyncThunk(
    "referral/getReferralTree",
    async (orderNo: string, { rejectWithValue }) => {
        try {
            const response = await api.get<ReferralTreeResponse>(
                `/api/v1/referral-mlm/customer/my-referral-earnings/flow-commission/${orderNo}/tree`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch referral tree data"
            );
        }
    }
);

export const getPartnerGrowthReferrals = createAsyncThunk(
    "referral/getPartnerGrowthReferrals",
    async (params: PartnerGrowthRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<PartnerGrowthResponse>(
                "/api/v1/kyc/partner-growth/customer-referrals",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch partner growth referrals"
            );
        }
    }
);

export const getPartnerGrowthReferralIncentives = createAsyncThunk(
    "referral/getPartnerGrowthReferralIncentives",
    async (params: PartnerGrowthIncentivesRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<PartnerGrowthIncentivesResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/partner-growth-referral-incentives",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch partner growth referral incentives"
            );
        }
    }
);

export const getFlowCommissionMonthlySummary = createAsyncThunk(
    "referral/getFlowCommissionMonthlySummary",
    async (params: FlowCommissionMonthlyRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<FlowCommissionMonthlyResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/flow-commission/monthly",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch flow commission monthly summary"
            );
        }
    }
);

export const getShoppingVouchers = createAsyncThunk(
    "referral/getShoppingVouchers",
    async (params: ShoppingVoucherRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<ShoppingVoucherResponse>(
                "/api/v1/referral-mlm/customer/shopping-vouchers/vouchers",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch shopping vouchers"
            );
        }
    }
);

export const getLoyaltyReferralCommission = createAsyncThunk(
    "referral/getLoyaltyReferralCommission",
    async (params: LoyaltyReferralCommissionRequest, { rejectWithValue }) => {
        try {
            const response = await api.get<LoyaltyReferralCommissionResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/loyalty-referral-commission",
                { params }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch loyalty referral commission data"
            );
        }
    }
);

export const getReferralEarningsSummary = createAsyncThunk(
    "referral/getReferralEarningsSummary",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get<ReferralEarningsSummaryResponse>(
                "/api/v1/referral-mlm/customer/my-referral-earnings/summary"
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch referral earnings summary"
            );
        }
    }
);

export const getReferralNetworkMilestoneMembers = createAsyncThunk(
    "referral/getReferralNetworkMilestoneMembers",
    async ({ orderNo, milestoneNo, ...params }: ReferralNetworkMilestoneMembersRequest, { rejectWithValue }) => {
        try {
            const searchVal = params.search || "";
            const apiParams: any = {
                page: params.page || 1,
                limit: params.limit || 10,
                sortOrder: params.sortOrder || "DESC",
                status: params.status || "ALL",
            };
            if (searchVal) {
                apiParams.search = searchVal;
                if (/^[sS]\d/.test(searchVal)) {
                    apiParams.registerId = searchVal;
                } else {
                    apiParams.memberName = searchVal;
                }
            }

            // 1. Try dedicated milestone members endpoint
            try {
                const res = await api.get(
                    `/api/v1/referral-mlm/customer/my-referral-earnings/flow-commission/${orderNo}/referral-network/members/milestone/${milestoneNo}`,
                    { params: apiParams }
                );
                const resData = res.data.data || res.data;
                if (resData && (resData.members || resData.milestone)) {
                    return {
                        success: true,
                        milestone: resData.milestone,
                        members: resData.members || [],
                        pagination: resData.pagination || {
                            page: params.page || 1,
                            limit: params.limit || 10,
                            total: resData.members?.length || 0,
                            totalPages: Math.ceil((resData.members?.length || 0) / (params.limit || 10)) || 1
                        }
                    };
                }
            } catch (endpointErr) {
                console.warn("Primary milestone members API fallback:", endpointErr);
            }

            // 2. Fallback to order-network-details if needed
            const response = await api.get(
                `/api/v1/referral-mlm/customer/my-referral-earnings/order-network-details/${orderNo}`,
                { params: { ...apiParams, level: milestoneNo } }
            );

            const data = response.data.data || response.data;
            const ownerRegisterId = data.ownerDetails?.registerId;
            const ownerUserName = data.ownerDetails?.fullName;

            let members: any[] = [];
            let milestoneInfo: any = null;

            if (data.referralNetwork) {
                const W = data.referralNetwork.width || 2;
                const lvl = milestoneNo;
                const req = Math.pow(W, lvl);

                const allMembers = data.referralNetwork.members || [];
                const sortedMembers = [...allMembers].sort((a: any, b: any) => {
                    if (a.level !== b.level) return a.level - b.level;
                    return a.position - b.position;
                });

                const lvlMembers = sortedMembers.filter((m: any) => m.level === lvl);
                const joined = lvlMembers.length;
                const levelCommissions = data.referralProduct?.levelCommissions || [];
                const lvlConfig = levelCommissions.find((lc: any) => lc.level === lvl) || {};

                milestoneInfo = {
                    milestoneNo: lvl,
                    level: lvl,
                    contributionRequirement: req,
                    activeContributors: joined,
                    status: joined >= req ? "Completed" : "Pending",
                    isCompleted: joined >= req,
                    isPending: joined < req,
                    salarFlowPerPerson: lvlConfig.salarFlowPerPerson || lvlConfig.salarFlowCommission || 0,
                    salarPearlsPerPerson: lvlConfig.salarPearlsPerPerson || lvlConfig.salarPearlsCommission || 0,
                    isAutoUpgrade: false,
                    productName: data.productDetails?.productName || data.referralNetwork?.productName || "PRODUCT 1",
                };

                members = lvlMembers.map((member: any, idx: number) => {
                    const pRegId = member.referringPartnerId || member.parentRegisterId || member.uplinerRegisterId || ownerRegisterId || "—";
                    const pName = member.referralUplinerName || member.uplinerName || ownerUserName || "—";

                    return {
                        sNo: idx + 1,
                        memberId: member.userId,
                        memberName: member.userName,
                        memberPhoto: "",
                        partnerNetworkId: member.registerId,
                        referringPartnerId: pRegId,
                        referralUplinerId: pRegId,
                        referralUplinerName: pName,
                        joinedDate: member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-GB') : "—",
                        joinedDateRaw: member.createdAt || "",
                        level: member.level,
                        status: "Completed",
                        receivedIncentiveDetails: {
                            salarFlowIncentives: member.receivedIncentiveDetails?.salarFlowIncentives ?? member.commissionAmount ?? (lvlConfig.salarFlowPerPerson || 0),
                            salarPearlsIncentives: member.receivedIncentiveDetails?.salarPearlsIncentives ?? member.salarCoins ?? (lvlConfig.salarPearlsPerPerson || 0),
                            novaReferralCommission: member.receivedIncentiveDetails?.novaReferralCommission ?? member.salarCoins ?? 0,
                        }
                    };
                });
            } else if (data.novaNetwork) {
                const lvl = milestoneNo;
                const levelsSummary = data.novaNetwork.levelsSummary || [];
                const summary = levelsSummary.find((s: any) => s.levelNo === lvl);
                const req = summary?.requiredMembers || 0;
                const joined = summary?.joinedMembers || 0;

                milestoneInfo = {
                    milestoneNo: lvl,
                    level: lvl,
                    contributionRequirement: req,
                    activeContributors: joined,
                    status: summary?.status === "COMPLETED" ? "Completed" : "Pending",
                    isCompleted: summary?.status === "COMPLETED",
                    isPending: summary?.status !== "COMPLETED",
                    salarFlowPerPerson: summary?.commissionAmount || 0,
                    salarPearlsPerPerson: summary?.salarCoins || 0,
                    isAutoUpgrade: true,
                    productName: data.novaNetwork.productName,
                };

                const lvlMembers = (data.novaNetwork.members || []).filter((m: any) => m.level === lvl || !lvl);
                members = lvlMembers.map((member: any, idx: number) => {
                    const pRegId = member.referringPartnerId || member.parentRegisterId || member.uplinerRegisterId || ownerRegisterId || "—";
                    const pName = member.referralUplinerName || member.uplinerName || ownerUserName || "—";

                    return {
                        sNo: idx + 1,
                        memberId: member.userId,
                        memberName: member.userName,
                        memberPhoto: "",
                        partnerNetworkId: member.registerId,
                        referringPartnerId: pRegId,
                        referralUplinerId: pRegId,
                        referralUplinerName: pName,
                        joinedDate: member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-GB') : "—",
                        joinedDateRaw: member.createdAt || "",
                        level: member.level || lvl,
                        status: "Completed",
                        receivedIncentiveDetails: {
                            salarFlowIncentives: member.receivedIncentiveDetails?.salarFlowIncentives ?? member.commissionAmount ?? (summary?.commissionAmount || 0),
                            salarPearlsIncentives: member.receivedIncentiveDetails?.salarPearlsIncentives ?? member.salarCoins ?? (summary?.salarCoins || 0),
                            novaReferralCommission: member.receivedIncentiveDetails?.novaReferralCommission ?? member.salarCoins ?? (summary?.commission || summary?.commissionAmount || 0),
                        }
                    };
                });
            }

            const page = params.page || 1;
            const limit = params.limit || 10;
            const total = members.length;
            const paginatedMembers = members.slice((page - 1) * limit, page * limit);

            return {
                success: true,
                milestone: milestoneInfo,
                members: paginatedMembers,
                pagination: {
                    page,
                    limit,
                    total,
                    totalPages: Math.ceil(total / limit),
                }
            } as any;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch milestone members"
            );
        }
    }
);

const initialState: ReferralState = {
    pearlsCommission: {
        data: [],
        pagination: null,
        totalPearlsReferralCommission: 0,
        loading: false,
        error: null,
        success: false,
    },
    pearlsCommissionDetail: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    infinityCommission: {
        data: [],
        pagination: null,
        totals: null,
        loading: false,
        error: null,
        success: false,
    },
    // Adding details state for infinity
    infinityCommissionDetails: {
        data: [],
        pagination: null,
        loading: false,
        error: null,
        success: false,
    },
    unityCommission: {
        data: [],
        pagination: null,
        totalUnityCommission: 0,
        loading: false,
        error: null,
        success: false,
    },
    communityGrowthCommission: {
        data: [],
        pagination: null,
        loading: false,
        error: null,
        success: false,
    },
    milestoneCommission: {
        data: [],
        pagination: null,
        totalMilestoneCommission: 0,
        loading: false,
        error: null,
        success: false,
    },
    milestoneCommissionDetail: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    referralCommission: {
        data: [],
        pagination: null,
        totalReferralCommission: 0,
        loading: false,
        error: null,
        success: false,
    },
    dailyCommission: {
        data: [],
        pagination: null,
        totals: null,
        loading: false,
        error: null,
        success: false,
    },
    // Adding details state for daily
    dailyCommissionDetails: {
        data: [],
        pagination: null,
        monthSummary: null,
        loading: false,
        error: null,
        success: false,
    },
    partnerGrowth: {
        data: [],
        pagination: null,
        loading: false,
        error: null,
        success: false,
    },
    flowCommission: {
        data: [],
        pagination: null,
        totalFlowIncentives: 0,
        loading: false,
        error: null,
        success: false,
    },
    referralNetwork: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    novaReferralNetwork: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    referralTree: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    partnerGrowthIncentives: {
        data: [],
        pagination: null,
        totals: null,
        loading: false,
        error: null,
        success: false,
    },
    referralNetworkMilestoneMembers: {
        members: [],
        pagination: null,
        milestone: null,
        loading: false,
        error: null,
        success: false,
    },
    flowCommissionMonthly: {
        data: [],
        pagination: null,
        loading: false,
        error: null,
        success: false,
    },
    referralEarningsSummary: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    loyaltyReferralCommission: {
        data: [],
        pagination: null,
        totalLoyaltyReferralCommission: 0,
        loading: false,
        error: null,
        success: false,
    },
    shoppingVouchers: {
        data: [],
        pagination: null,
        wallet: null,
        loading: false,
        error: null,
        success: false,
    },
};

const referralSlice = createSlice({
    name: "referral",
    initialState,
    reducers: {
        clearPearlsCommissionState: (state) => {
            state.pearlsCommission.loading = false;
            state.pearlsCommission.error = null;
            state.pearlsCommission.success = false;
        },
        clearPearlsCommissionDetailState: (state) => {
            state.pearlsCommissionDetail.data = null;
            state.pearlsCommissionDetail.loading = false;
            state.pearlsCommissionDetail.error = null;
            state.pearlsCommissionDetail.success = false;
        },
        clearMilestoneCommissionDetailState: (state) => {
            state.milestoneCommissionDetail.data = null;
            state.milestoneCommissionDetail.loading = false;
            state.milestoneCommissionDetail.error = null;
            state.milestoneCommissionDetail.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPearlsCommission.pending, (state) => {
                state.pearlsCommission.loading = true;
                state.pearlsCommission.error = null;
                state.pearlsCommission.success = false;
            })
            .addCase(
                getPearlsCommission.fulfilled,
                (state, action: PayloadAction<PearlsCommissionResponse>) => {
                    state.pearlsCommission.loading = false;
                    state.pearlsCommission.success = true;
                    state.pearlsCommission.data = action.payload.data;
                    state.pearlsCommission.pagination = action.payload.pagination;
                    state.pearlsCommission.totalPearlsReferralCommission =
                        action.payload.totalPearlsReferralCommission;
                }
            )
            .addCase(getPearlsCommission.rejected, (state, action) => {
                state.pearlsCommission.loading = false;
                state.pearlsCommission.error = action.payload as string;
            })
            // Pearls Commission Detail
            .addCase(getPearlsCommissionDetail.pending, (state) => {
                state.pearlsCommissionDetail.loading = true;
                state.pearlsCommissionDetail.error = null;
                state.pearlsCommissionDetail.success = false;
            })
            .addCase(
                getPearlsCommissionDetail.fulfilled,
                (state, action: PayloadAction<PearlsCommissionDetailResponse>) => {
                    state.pearlsCommissionDetail.loading = false;
                    state.pearlsCommissionDetail.success = true;
                    state.pearlsCommissionDetail.data = action.payload.data;
                }
            )
            .addCase(getPearlsCommissionDetail.rejected, (state, action) => {
                state.pearlsCommissionDetail.loading = false;
                state.pearlsCommissionDetail.error = action.payload as string;
            })
            // Infinity Commission
            .addCase(getInfinityCommission.pending, (state) => {
                state.infinityCommission.loading = true;
                state.infinityCommission.error = null;
                state.infinityCommission.success = false;
            })
            .addCase(
                getInfinityCommission.fulfilled,
                (state, action: PayloadAction<InfinityCommissionResponse>) => {
                    state.infinityCommission.loading = false;
                    state.infinityCommission.success = true;
                    state.infinityCommission.data = action.payload.data;
                    state.infinityCommission.pagination = action.payload.pagination;
                    state.infinityCommission.totals = action.payload.totals;
                }
            )
            .addCase(getInfinityCommission.rejected, (state, action) => {
                state.infinityCommission.loading = false;
                state.infinityCommission.error = action.payload as string;
            })
            // Infinity Commission Details
            .addCase(getInfinityCommissionDetails.pending, (state) => {
                state.infinityCommissionDetails.loading = true;
                state.infinityCommissionDetails.error = null;
                state.infinityCommissionDetails.success = false;
            })
            .addCase(
                getInfinityCommissionDetails.fulfilled,
                (state, action: PayloadAction<InfinityCommissionDetailResponse>) => {
                    state.infinityCommissionDetails.loading = false;
                    state.infinityCommissionDetails.success = true;
                    state.infinityCommissionDetails.data = action.payload.data;
                    state.infinityCommissionDetails.pagination = action.payload.pagination;
                }
            )
            .addCase(getInfinityCommissionDetails.rejected, (state, action) => {
                state.infinityCommissionDetails.loading = false;
                state.infinityCommissionDetails.error = action.payload as string;
            })
            // Unity Commission
            .addCase(getUnityCommission.pending, (state) => {
                state.unityCommission.loading = true;
                state.unityCommission.error = null;
                state.unityCommission.success = false;
            })
            .addCase(
                getUnityCommission.fulfilled,
                (state, action: PayloadAction<UnityCommissionResponse>) => {
                    state.unityCommission.loading = false;
                    state.unityCommission.success = true;
                    state.unityCommission.data = action.payload.data;
                    state.unityCommission.pagination = action.payload.pagination;
                    state.unityCommission.totalUnityCommission = action.payload.totalUnityCommission;
                }
            )
            .addCase(getUnityCommission.rejected, (state, action) => {
                state.unityCommission.loading = false;
                state.unityCommission.error = action.payload as string;
            })
            // Community Growth Commission
            .addCase(getCommunityGrowthCommission.pending, (state) => {
                state.communityGrowthCommission.loading = true;
                state.communityGrowthCommission.error = null;
                state.communityGrowthCommission.success = false;
            })
            .addCase(
                getCommunityGrowthCommission.fulfilled,
                (state, action: PayloadAction<CommunityGrowthCommissionResponse>) => {
                    state.communityGrowthCommission.loading = false;
                    state.communityGrowthCommission.success = true;
                    state.communityGrowthCommission.data = action.payload.data;
                    state.communityGrowthCommission.pagination = action.payload.pagination;
                }
            )
            .addCase(getCommunityGrowthCommission.rejected, (state, action) => {
                state.communityGrowthCommission.loading = false;
                state.communityGrowthCommission.error = action.payload as string;
            })
            // Milestone Commission
            .addCase(getMilestoneCommission.pending, (state) => {
                state.milestoneCommission.loading = true;
                state.milestoneCommission.error = null;
                state.milestoneCommission.success = false;
            })
            .addCase(
                getMilestoneCommission.fulfilled,
                (state, action: PayloadAction<MilestoneCommissionResponse>) => {
                    state.milestoneCommission.loading = false;
                    state.milestoneCommission.success = true;
                    state.milestoneCommission.data = action.payload.data;
                    state.milestoneCommission.pagination = action.payload.pagination;
                    state.milestoneCommission.totalMilestoneCommission =
                        action.payload.totalMilestoneReferralCommission;
                }
            )
            .addCase(getMilestoneCommission.rejected, (state, action) => {
                state.milestoneCommission.loading = false;
                state.milestoneCommission.error = action.payload as string;
            })
            // Milestone Commission Detail
            .addCase(getMilestoneCommissionDetail.pending, (state) => {
                state.milestoneCommissionDetail.loading = true;
                state.milestoneCommissionDetail.error = null;
                state.milestoneCommissionDetail.success = false;
            })
            .addCase(
                getMilestoneCommissionDetail.fulfilled,
                (state, action: PayloadAction<MilestoneCommissionDetailResponse>) => {
                    state.milestoneCommissionDetail.loading = false;
                    state.milestoneCommissionDetail.success = true;
                    state.milestoneCommissionDetail.data = action.payload.data;
                }
            )
            .addCase(getMilestoneCommissionDetail.rejected, (state, action) => {
                state.milestoneCommissionDetail.loading = false;
                state.milestoneCommissionDetail.error = action.payload as string;
            })
            // Referral Commission
            .addCase(getReferralCommission.pending, (state) => {
                state.referralCommission.loading = true;
                state.referralCommission.error = null;
                state.referralCommission.success = false;
            })
            .addCase(
                getReferralCommission.fulfilled,
                (state, action: PayloadAction<ReferralCommissionResponse>) => {
                    state.referralCommission.loading = false;
                    state.referralCommission.success = true;
                    state.referralCommission.data = action.payload.data;
                    state.referralCommission.pagination = action.payload.pagination;
                    state.referralCommission.totalReferralCommission =
                        action.payload.totalReferralCommission;
                }
            )
            .addCase(getReferralCommission.rejected, (state, action) => {
                state.referralCommission.loading = false;
                state.referralCommission.error = action.payload as string;
            })
            // Daily Commission
            .addCase(getDailyCommission.pending, (state) => {
                state.dailyCommission.loading = true;
                state.dailyCommission.error = null;
                state.dailyCommission.success = false;
            })
            .addCase(
                getDailyCommission.fulfilled,
                (state, action: PayloadAction<DailyCommissionResponse>) => {
                    state.dailyCommission.loading = false;
                    state.dailyCommission.success = true;
                    state.dailyCommission.data = action.payload.data;
                    state.dailyCommission.pagination = action.payload.pagination;
                    state.dailyCommission.totals = action.payload.totals;
                }
            )
            .addCase(getDailyCommission.rejected, (state, action) => {
                state.dailyCommission.loading = false;
                state.dailyCommission.error = action.payload as string;
            })
            // Daily Commission Details
            .addCase(getDailyCommissionDetails.pending, (state) => {
                state.dailyCommissionDetails.loading = true;
                state.dailyCommissionDetails.error = null;
                state.dailyCommissionDetails.success = false;
            })
            .addCase(
                getDailyCommissionDetails.fulfilled,
                (state, action: PayloadAction<DailyCommissionDetailResponse>) => {
                    state.dailyCommissionDetails.loading = false;
                    state.dailyCommissionDetails.success = true;
                    state.dailyCommissionDetails.data = action.payload.data;
                    state.dailyCommissionDetails.pagination = action.payload.pagination;
                    state.dailyCommissionDetails.monthSummary = action.payload.monthSummary;
                }
            )
            .addCase(getDailyCommissionDetails.rejected, (state, action) => {
                state.dailyCommissionDetails.loading = false;
                state.dailyCommissionDetails.error = action.payload as string;
            })
            // Flow Commission
            .addCase(getFlowCommission.pending, (state) => {
                state.flowCommission.loading = true;
                state.flowCommission.error = null;
                state.flowCommission.success = false;
            })
            .addCase(
                getFlowCommission.fulfilled,
                (state, action: PayloadAction<FlowCommissionResponse>) => {
                    state.flowCommission.loading = false;
                    state.flowCommission.success = true;
                    state.flowCommission.data = action.payload.data;
                    state.flowCommission.pagination = action.payload.pagination;
                    state.flowCommission.totalFlowIncentives = action.payload.totalFlowIncentives;
                }
            )
            .addCase(getFlowCommission.rejected, (state, action) => {
                state.flowCommission.loading = false;
                state.flowCommission.error = action.payload as string;
            })
            // Referral Network
            .addCase(getReferralNetwork.pending, (state) => {
                state.referralNetwork.loading = true;
                state.referralNetwork.error = null;
                state.referralNetwork.success = false;
            })
            .addCase(
                getReferralNetwork.fulfilled,
                (state, action: PayloadAction<ReferralNetworkResponse>) => {
                    state.referralNetwork.loading = false;
                    state.referralNetwork.success = true;
                    state.referralNetwork.data = action.payload;
                }
            )
            .addCase(getReferralNetwork.rejected, (state, action) => {
                state.referralNetwork.loading = false;
                state.referralNetwork.error = action.payload as string;
            })
            // Nova Referral Network
            .addCase(getNovaReferralNetwork.pending, (state) => {
                state.novaReferralNetwork.loading = true;
                state.novaReferralNetwork.error = null;
                state.novaReferralNetwork.success = false;
            })
            .addCase(
                getNovaReferralNetwork.fulfilled,
                (state, action: PayloadAction<NovaReferralNetworkResponse>) => {
                    state.novaReferralNetwork.loading = false;
                    state.novaReferralNetwork.success = true;
                    state.novaReferralNetwork.data = action.payload;
                }
            )
            .addCase(getNovaReferralNetwork.rejected, (state, action) => {
                state.novaReferralNetwork.loading = false;
                state.novaReferralNetwork.error = action.payload as string;
            })
            // Referral Tree
            .addCase(getReferralTree.pending, (state) => {
                state.referralTree.loading = true;
                state.referralTree.error = null;
                state.referralTree.success = false;
            })
            .addCase(
                getReferralTree.fulfilled,
                (state, action: PayloadAction<ReferralTreeResponse>) => {
                    state.referralTree.loading = false;
                    state.referralTree.success = true;
                    state.referralTree.data = action.payload;
                }
            )
            .addCase(getReferralTree.rejected, (state, action) => {
                state.referralTree.loading = false;
                state.referralTree.error = action.payload as string;
            })
            // Partner Growth Referrals
            .addCase(getPartnerGrowthReferrals.pending, (state) => {
                state.partnerGrowth.loading = true;
                state.partnerGrowth.error = null;
                state.partnerGrowth.success = false;
            })
            .addCase(
                getPartnerGrowthReferrals.fulfilled,
                (state, action: PayloadAction<PartnerGrowthResponse>) => {
                    state.partnerGrowth.loading = false;
                    state.partnerGrowth.success = true;
                    state.partnerGrowth.data = action.payload.data;
                    state.partnerGrowth.pagination = {
                        page: action.payload.meta.page,
                        limit: action.payload.meta.limit,
                        total: action.payload.meta.total,
                        totalPages: action.payload.meta.totalPages,
                    };
                }
            )
            .addCase(getPartnerGrowthReferrals.rejected, (state, action) => {
                state.partnerGrowth.loading = false;
                state.partnerGrowth.error = action.payload as string;
            })
            // Partner Growth Referral Incentives
            .addCase(getPartnerGrowthReferralIncentives.pending, (state) => {
                state.partnerGrowthIncentives.loading = true;
                state.partnerGrowthIncentives.error = null;
                state.partnerGrowthIncentives.success = false;
            })
            .addCase(
                getPartnerGrowthReferralIncentives.fulfilled,
                (state, action: PayloadAction<PartnerGrowthIncentivesResponse>) => {
                    state.partnerGrowthIncentives.loading = false;
                    state.partnerGrowthIncentives.success = true;
                    state.partnerGrowthIncentives.data = action.payload.data;
                    state.partnerGrowthIncentives.pagination = action.payload.pagination;
                    state.partnerGrowthIncentives.totals = action.payload.totals;
                }
            )
            .addCase(getPartnerGrowthReferralIncentives.rejected, (state, action) => {
                state.partnerGrowthIncentives.loading = false;
                state.partnerGrowthIncentives.error = action.payload as string;
            })
            // Referral Network Milestone Members
            .addCase(getReferralNetworkMilestoneMembers.pending, (state) => {
                state.referralNetworkMilestoneMembers.loading = true;
                state.referralNetworkMilestoneMembers.error = null;
                state.referralNetworkMilestoneMembers.success = false;
            })
            .addCase(
                getReferralNetworkMilestoneMembers.fulfilled,
                (state, action: PayloadAction<ReferralNetworkMilestoneMembersResponse>) => {
                    state.referralNetworkMilestoneMembers.loading = false;
                    state.referralNetworkMilestoneMembers.success = true;
                    state.referralNetworkMilestoneMembers.members = action.payload.members;
                    state.referralNetworkMilestoneMembers.pagination = action.payload.pagination;
                    state.referralNetworkMilestoneMembers.milestone = action.payload.milestone;
                }
            )
            .addCase(getReferralNetworkMilestoneMembers.rejected, (state, action) => {
                state.referralNetworkMilestoneMembers.loading = false;
                state.referralNetworkMilestoneMembers.error = action.payload as string;
            })
            // Flow Commission Monthly Summary
            .addCase(getFlowCommissionMonthlySummary.pending, (state) => {
                state.flowCommissionMonthly.loading = true;
                state.flowCommissionMonthly.error = null;
                state.flowCommissionMonthly.success = false;
            })
            .addCase(
                getFlowCommissionMonthlySummary.fulfilled,
                (state, action: PayloadAction<FlowCommissionMonthlyResponse>) => {
                    state.flowCommissionMonthly.loading = false;
                    state.flowCommissionMonthly.success = true;
                    state.flowCommissionMonthly.data = action.payload.data;
                    state.flowCommissionMonthly.pagination = action.payload.pagination;
                }
            )
            .addCase(getFlowCommissionMonthlySummary.rejected, (state, action) => {
                state.flowCommissionMonthly.loading = false;
                state.flowCommissionMonthly.error = action.payload as string;
            })
            // Shopping Vouchers
            .addCase(getShoppingVouchers.pending, (state) => {
                state.shoppingVouchers.loading = true;
                state.shoppingVouchers.error = null;
                state.shoppingVouchers.success = false;
            })
            .addCase(
                getShoppingVouchers.fulfilled,
                (state, action: PayloadAction<ShoppingVoucherResponse>) => {
                    state.shoppingVouchers.loading = false;
                    state.shoppingVouchers.success = true;
                    state.shoppingVouchers.data = action.payload.data;
                    state.shoppingVouchers.pagination = action.payload.pagination;
                    state.shoppingVouchers.wallet = action.payload.wallet;
                }
            )
            .addCase(getShoppingVouchers.rejected, (state, action) => {
                state.shoppingVouchers.loading = false;
                state.shoppingVouchers.error = action.payload as string;
            })
            // Loyalty Referral Commission
            .addCase(getLoyaltyReferralCommission.pending, (state) => {
                state.loyaltyReferralCommission.loading = true;
                state.loyaltyReferralCommission.error = null;
                state.loyaltyReferralCommission.success = false;
            })
            .addCase(
                getLoyaltyReferralCommission.fulfilled,
                (state, action: PayloadAction<LoyaltyReferralCommissionResponse>) => {
                    state.loyaltyReferralCommission.loading = false;
                    state.loyaltyReferralCommission.success = true;
                    state.loyaltyReferralCommission.data = action.payload.data;
                    state.loyaltyReferralCommission.pagination = action.payload.pagination;
                    state.loyaltyReferralCommission.totalLoyaltyReferralCommission =
                        action.payload.totalLoyaltyReferralCommission;
                }
            )
            .addCase(getLoyaltyReferralCommission.rejected, (state, action) => {
                state.loyaltyReferralCommission.loading = false;
                state.loyaltyReferralCommission.error = action.payload as string;
            })
            // Referral Earnings Summary
            .addCase(getReferralEarningsSummary.pending, (state) => {
                state.referralEarningsSummary.loading = true;
                state.referralEarningsSummary.error = null;
                state.referralEarningsSummary.success = false;
            })
            .addCase(
                getReferralEarningsSummary.fulfilled,
                (state, action: PayloadAction<ReferralEarningsSummaryResponse>) => {
                    state.referralEarningsSummary.loading = false;
                    state.referralEarningsSummary.success = true;
                    state.referralEarningsSummary.data = action.payload.data;
                }
            )
            .addCase(getReferralEarningsSummary.rejected, (state, action) => {
                state.referralEarningsSummary.loading = false;
                state.referralEarningsSummary.error = action.payload as string;
            });
    },
});

export const {
    clearPearlsCommissionState,
    clearPearlsCommissionDetailState,
    clearMilestoneCommissionDetailState,
} = referralSlice.actions;
export default referralSlice.reducer;
